import { Form, Modal, Space, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import {
    loanDrawdownGetAction,
    loanDrawdownProcessAction,
} from '~Loans/actions';
import { loanDrawdownSelector } from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import { IDictionary } from '~utilities/IDictionary';

export default function DrawdownProcessModal(props: {
    loanDrawdownUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { loanDrawdownUuid, isOpen, onCancel } = props;

    const drawdown: ILoanDrawdown = useSelector((state: IGlobalState) => loanDrawdownSelector(state, loanDrawdownUuid));

    const [ processedDate, setProcessedDate ] = useState<string>(null);
    const [ errors, setErrors ] = useState<IDictionary<string>>({});

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!drawdown) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        dispatch,
        drawdown,
        loanDrawdownUuid,
    ]);

    const validateProcessedDate: () => boolean = useCallback(() => {
        let error: string = null;

        if (!processedDate) {
            error = 'Please enter the processed date';
        }

        setErrors({
            ...errors,
            processedDate: error,
        });

        return !error;
    }, [
        errors,
        processedDate,
    ]);

    const disabledTime: (time: Dayjs) => boolean = useCallback((time: Dayjs) => {
        return time && (time.isAfter(dayjs(), 'second') || time.isBefore(dayjs(drawdown.disbursalInstructedTime)));
    }, [
        drawdown.disbursalInstructedTime,
    ]);

    const onClickOk: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateProcessedDate() && valid;

        if (!valid) {
            return;
        }

        dispatch(loanDrawdownProcessAction(drawdown.uuid, processedDate));
        onCancel();
    }, [
        dispatch,
        drawdown,
        onCancel,
        processedDate,
        validateProcessedDate,
    ]);

    const onChangeProcessedDate: (date: Dayjs) => void = useCallback((date: Dayjs) => {
        setProcessedDate(date ? dayjs(`${date.format('YYYY-MM-DD')} ${dayjs(processedDate || undefined).format('HH:mm')}`).format() : null);
    }, [
        processedDate,
    ]);

    const onChangeProcessedTime: (time: Dayjs) => void = useCallback((time: Dayjs) => {
        setProcessedDate(time ? dayjs(`${dayjs(processedDate || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format() : null);
    }, [
        processedDate,
    ]);

    if (!drawdown) {
        return (
            <Modal
                footer={false}
                onCancel={onCancel}
                open={isOpen}
                title='Disbursal Instructed'
                wrapClassName='loan-drawdown-disbursal-instructed-modal'
                width={640}
            >
                <Spin/>
            </Modal>
        );
    }

    return (
        <Modal
            okText='Process'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Process'
            wrapClassName='loan-drawdown-disbursal-processed-modal'
            width={640}
        >
            <Form.Item
                label='Processed Date'
                className='procssed-date'
                help={errors.processedDate}
                validateStatus={errors.processedDate && 'error'}
            >
                <Space>
                    <DatePicker
                        className='processed-date-pick-date'
                        disabledDate={disabledTime}
                        onChange={onChangeProcessedDate}
                        format='DD/MM/YYYY'
                        value={processedDate ? dayjs(processedDate) : null}
                    />
                    <TimePicker
                        className='processed-date-pick-time'
                        disabledDate={disabledTime}
                        onChange={onChangeProcessedTime}
                        value={processedDate ? dayjs(processedDate) : null}
                        format='H:mm'
                    />
                </Space>
            </Form.Item>
        </Modal>
    );
}